import type { ComponentType, ReactNode } from 'react';

import { ActionIcon, Menu, Stack } from '@mantine/core';
import type { MantineColor } from '@mantine/core';

import {
  IconClipboardList,
  IconClock,
  IconCoins,
  IconDots,
  IconLayoutDashboard,
  IconTag,
} from '@tabler/icons-react';

import { CardView } from '../../cards/Card';
import type { LabeledValueListItem } from '../../common/molecules/LabeledValueList';
import { LabeledValueListView } from '../../common/molecules/LabeledValueList';
import { DetailDrawerView } from '../../detail-drawer';

export interface TaskDetailDrawerMenuItem {
  label: string;
  icon: ComponentType<{ size?: number | string }>;
  onClick: () => void;
  color?: MantineColor;
}

export interface TaskDetailDrawerViewProps {
  opened: boolean;
  onClose: () => void;
  loading?: boolean;
  titleValue: string;
  onTitleChange: (value: string) => void;
  /** Sticky footer action, e.g. a Save button — stays reachable regardless of scroll position. */
  saveAction?: ReactNode;
  /** Actions menu items (archive/delete etc.) — the dots trigger and dropdown are built here. */
  menuItems?: TaskDetailDrawerMenuItem[];
  /**
   * Descriptive metadata shown below the key figures — status/projekt (read-only, no `editor`)
   * plus kategori/milepæl/lokation (inline-editable via `editor`).
   */
  metadata: LabeledValueListItem[];
  activeTab: string;
  onTabChange: (value: string) => void;
  overviewContent: ReactNode;
  timeContent: ReactNode;
  economyContent: ReactNode;
}

export function TaskDetailDrawerView({
  opened,
  onClose,
  loading = false,
  titleValue,
  onTitleChange,
  saveAction,
  menuItems,
  metadata,
  activeTab,
  onTabChange,
  overviewContent,
  timeContent,
  economyContent,
}: TaskDetailDrawerViewProps) {
  return (
    <DetailDrawerView
      opened={opened}
      onClose={onClose}
      loading={loading}
      icon={IconClipboardList}
      titleValue={titleValue}
      onTitleChange={onTitleChange}
      footer={saveAction}
      menu={
        menuItems && menuItems.length > 0 ? (
          <Menu
            position="bottom-end"
            withinPortal
            transitionProps={{ transition: 'pop-top-right' }}
          >
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDots size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {menuItems.map(({ label, icon: ItemIcon, onClick, color }) => (
                <Menu.Item
                  key={label}
                  leftSection={<ItemIcon size={16} />}
                  color={color}
                  onClick={onClick}
                >
                  {label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        ) : undefined
      }
      activeTab={activeTab}
      onTabChange={onTabChange}
      tabs={[
        {
          value: 'overview',
          label: 'Overblik',
          icon: IconLayoutDashboard,
          content: (
            <Stack gap="md">
              <CardView icon={{ icon: IconTag, color: 'grape' }} title="Detaljer">
                <LabeledValueListView items={metadata} variant="property" />
              </CardView>
              {overviewContent}
            </Stack>
          ),
        },
        { value: 'time', label: 'Tid', icon: IconClock, content: timeContent },
        { value: 'economy', label: 'Økonomi', icon: IconCoins, content: economyContent },
      ]}
    />
  );
}
